import { z } from 'zod';
import { INTAKE_FIELD_TYPES } from '../intake.js';
import { MAX_ASK_REASON } from './asks.js';
import { agencyOwned, matchChoice, questionChoices, unmatchedChoices, YES_NO_OPTIONS } from './choices.js';
import { fnv1a32hex, KEY_RE } from './keys.js';
import { unitDimension } from './measure.js';
import { QUESTION_TYPES, wordingAudienceSchema } from './types.js';
import { INTAKE_TYPE_OF_QUESTION } from './values.js';
/**
 * Ways to ask (spec 3.20): one question, several ways to ask it. A way may change the wording and help,
 * the agency's label, the input style, the unit, how options are named and ordered, and it may narrow the
 * options. It may never collect what the question cannot hold (no new options, no other kind of answer, no
 * other meaning): that is a different question. Here: an agency's own field as a way to ask
 * (agencyFieldVariant, Phases 1 to 5), the rule (variantAllowed, which is also the compatibility test of spec
 * 3.1), the code ways and the default way for an audience, a way named by its key (variantOf), the narrowed
 * yes/no way a search hit offers (narrowToOption), the choices a way offers (wayChoices), and the one
 * question -> intake field map (toIntakeField). The value conversions are convert.ts.
 */
export const VARIANT_INPUTS = ['default', 'list', 'pills', 'yes_no', 'text'];
const text = (max) => z.string().trim().min(1).max(max);
const labels = z.array(text(120)).max(300);
const wayDraftShape = {
    wording: text(300),
    help: z.string().max(1000).optional(),
    agency_label: text(200).optional(),
    input: z.enum(VARIANT_INPUTS).optional(),
    unit: text(120).optional(),
    option_labels: z.record(text(120), text(120)).optional(),
    option_order: labels.optional(),
    narrow_to: labels.optional(),
    audience: wordingAudienceSchema.optional(),
};
/** A way to ask as an editor sends it (POST /businesses/:bid/library/variants `way`). */
export const wayDraftSchema = z.object(wayDraftShape);
export const questionVariantSchema = z.object({
    key: text(200),
    question_key: z.string().regex(KEY_RE),
    ...wayDraftShape,
    field_type: z.enum(INTAKE_FIELD_TYPES).optional(),
    own_options: labels.optional(),
    own_columns: labels.optional(),
});
/** A code way to ask: `ways_to_ask[index]` of the question. */
export const codeVariantKey = (questionKey, index) => `${questionKey}~${index}`;
/** A question_variants row (Phase 6). */
export const dbVariantKey = (id) => `v:${id}`;
/** The question's own label as the way to ask (agency surfaces, or no wording for the audience). */
export const labelVariantKey = (questionKey) => `${questionKey}~label`;
/** An agency's own field as a way to ask (Phases 1 to 5). */
export const fieldVariantKey = (fieldId) => `f:${fieldId}`;
const ANY_BUT_FILES_AND_GRIDS = QUESTION_TYPES.filter((t) => t !== 'file' && t !== 'matrix');
/**
 * The question types an agency field of each intake type can ask, its answers then being answers to that
 * question: a text field asks anything but a file or a grid, as free text; a single-choice list asks a
 * choice (or free-text) question; every other field asks its own kind (or free text). A list of several
 * choices, a grid and a file ask only their own kind; display cards ask nothing.
 */
export const FIELD_CAN_ASK = Object.freeze({
    short_text: ANY_BUT_FILES_AND_GRIDS,
    long_text: ANY_BUT_FILES_AND_GRIDS,
    email: ['email', 'short_text', 'long_text', 'contact'],
    phone: ['phone', 'short_text', 'long_text', 'contact'],
    date: ['date', 'short_text', 'long_text'],
    number: ['number', 'measurement', 'short_text', 'long_text'],
    select: ['select', 'multi_select', 'yes_no', 'short_text', 'long_text'],
    multi_select: ['multi_select'],
    matrix: ['matrix'],
    file: ['file'],
    info: [],
    link: [],
});
const isTextField = (t) => t === 'short_text' || t === 'long_text';
/**
 * An agency's own field as a way to ask (Phases 1 to 5): its label and help as the wording, its intake type,
 * its choices in its own wording. A text field asking a question whose own input is not text (a choice, a
 * number, a date) is the free-text way to ask it (input 'text').
 */
export function agencyFieldVariant(q, field) {
    const choices = field.type === 'matrix' ? field.rows : field.options;
    return {
        key: fieldVariantKey(field.id),
        question_key: q.key,
        wording: field.label,
        ...(field.help ? { help: field.help } : {}),
        agency_label: field.label,
        input: isTextField(field.type) && !isTextField(INTAKE_TYPE_OF_QUESTION[q.type]) ? 'text' : 'default',
        field_type: field.type,
        ...(choices?.length ? { own_options: [...choices] } : {}),
        ...(field.type === 'matrix' && field.columns?.length ? { own_columns: [...field.columns] } : {}),
        audience: 'any',
    };
}
/** The one option a yes/no way asks about (yes/no on a single-option narrow of a list), else null. */
export function yesNoOption(q, v) {
    return v.input === 'yes_no' && (q.type === 'select' || q.type === 'multi_select') && v.narrow_to?.length === 1 ? v.narrow_to[0] : null;
}
/**
 * The choices a way offers, in its order, each with the label it shows: an agency's own field its own choices in
 * its own wording (each its own label); a yes/no way on one option Yes and No; else the question's choices (Yes and
 * No for a yes/no question, a list's options, a grid's rows), narrowed (narrow_to), ordered (option_order first) and
 * relabelled (option_labels) by the way. None for a free-text question.
 */
export function wayChoices(q, v) {
    if (v.own_options)
        return v.own_options.map((o) => ({ option: o, label: o }));
    if (v.input === 'yes_no' && q.type !== 'yes_no')
        return YES_NO_OPTIONS.map((o) => ({ option: o, label: o }));
    const all = questionChoices(q);
    const base = v.narrow_to && q.type !== 'yes_no' ? [...new Set(v.narrow_to)].filter((o) => all.includes(o)) : [...all];
    const order = [...new Set(v.option_order ?? [])].filter((o) => base.includes(o));
    return [...order, ...base.filter((o) => !order.includes(o))].map((o) => ({ option: o, label: v.option_labels?.[o] ?? o }));
}
const quoted = (names) => names.map((n) => `"${n}"`).join(', ');
/**
 * Null when `v` is an allowed way to ask `q`, else the reason. Also the compatibility test of spec 3.1: an
 * agency's field id takes a shared key only when agencyFieldVariant of each of its definitions is allowed.
 * - an agency field asks only what its intake type can (FIELD_CAN_ASK); free text never asks a file or a grid;
 * - yes/no only for a yes/no question or one option of a list; a list or pills only for a question with options;
 * - narrow_to, option_order and option_labels name only the question's own options (none for free text), and no
 *   two choices the way offers show the same label (an answer names exactly one option);
 * - a unit only for a number or a measurement, and only one of the same dimension (unitDimension);
 * - an agency's own choices and grid columns must each name one of the question's, unless the question is
 *   free text or each agency keeps its own (agencyOwned). Wording, help and labels never matter.
 */
export function variantAllowed(q, v) {
    if (v.question_key !== q.key)
        return `a way to ask ${v.question_key}, not ${q.key}`;
    const input = v.input ?? 'default';
    const choices = questionChoices(q);
    if (v.field_type && !FIELD_CAN_ASK[v.field_type].includes(q.type))
        return `a ${v.field_type} field cannot ask a ${q.type} question`;
    if (input === 'text' && (q.type === 'file' || q.type === 'matrix'))
        return `a ${q.type} question cannot be answered as free text`;
    if (input === 'yes_no' && q.type !== 'yes_no' && !yesNoOption(q, v))
        return 'yes or no asks only a yes/no question or one option of a list';
    if ((input === 'list' || input === 'pills') && !choices.length)
        return `a ${q.type} question has no options to list`;
    const named = [
        ['narrow_to', v.narrow_to],
        ['option_order', v.option_order],
        ['option_labels', v.option_labels && Object.keys(v.option_labels)],
    ];
    for (const [what, names] of named) {
        if (!names)
            continue;
        if (!choices.length)
            return `${what}: a ${q.type} question has no options`;
        const unknown = names.filter((n) => !choices.includes(n));
        if (unknown.length)
            return `${what} names options the question does not have: ${quoted(unknown)}`;
    }
    if (v.narrow_to && !v.narrow_to.length)
        return 'narrow_to keeps at least one option';
    if (v.option_labels) {
        const shown = wayChoices(q, v).map((c) => c.label);
        const twice = shown.filter((l, i) => shown.indexOf(l) !== i);
        if (twice.length)
            return `two options would show as ${quoted([...new Set(twice)])}`;
    }
    if (v.unit !== undefined) {
        if (q.type !== 'number' && q.type !== 'measurement')
            return `a ${q.type} question has no unit`;
        if (unitDimension(v.unit) !== unitDimension(q.unit))
            return `the unit "${v.unit}" does not measure what "${q.unit ?? q.label}" does`;
    }
    if (!agencyOwned(q)) {
        const rows = choices.length && v.own_options ? unmatchedChoices(choices, v.own_options, q.option_aliases) : [];
        if (rows.length)
            return `options the question cannot hold: ${quoted(rows)}`;
        const columns = v.own_columns ? unmatchedChoices(q.columns ?? [], v.own_columns) : [];
        if (columns.length)
            return `grid columns the question cannot hold: ${quoted(columns)}`;
    }
    return null;
}
/** Code way `index` of the question (`ways_to_ask[index]`, key `<key>~<index>`), or undefined. */
export function codeVariant(q, index) {
    const way = q.ways_to_ask[index];
    if (!way)
        return undefined;
    return {
        key: codeVariantKey(q.key, index),
        question_key: q.key,
        wording: way.wording,
        ...(way.help ? { help: way.help } : {}),
        agency_label: q.label,
        input: 'default',
        audience: way.audience,
    };
}
const labelVariant = (q, audience) => ({
    key: labelVariantKey(q.key),
    question_key: q.key,
    wording: q.label,
    agency_label: q.label,
    input: 'default',
    audience,
});
/**
 * The way to ask a question of an audience: its first way worded for that audience, else its first way
 * for anyone, else its label. For `agency` (agency surfaces: the schema, the client page) the label.
 */
export function defaultVariant(q, audience) {
    const i = audience === 'agency' ? -1 : [audience, 'any'].map((a) => q.ways_to_ask.findIndex((w) => w.audience === a)).find((n) => n >= 0) ?? -1;
    return codeVariant(q, i) ?? labelVariant(q, audience);
}
/**
 * The way a variant key names for `q`: a code way (`<key>~<n>`), the label (`<key>~label`), or a stored way
 * (`v:<id>`, looked up through `stored`: the agency's own and the shared question_variants) that asks this question
 * and is still allowed. Undefined for anything else, `f:<id>` included (an agency's own field is its own way:
 * agencyFieldVariant of that field).
 */
export function variantOf(q, variantKey, stored) {
    if (!variantKey)
        return undefined;
    if (variantKey === labelVariantKey(q.key))
        return labelVariant(q, 'agency');
    const code = variantKey.startsWith(`${q.key}~`) ? variantKey.slice(q.key.length + 1) : null;
    if (code !== null)
        return /^\d{1,4}$/.test(code) ? codeVariant(q, Number(code)) : undefined;
    if (!variantKey.startsWith('v:'))
        return undefined;
    const v = stored?.(variantKey);
    if (!v || variantAllowed(q, v) !== null)
        return undefined;
    return v.key === variantKey ? v : { ...v, key: variantKey };
}
/**
 * "Ask just this: <option>" (spec 3.19 option narrowing): a draft way asking one option of a list as yes or no (a
 * grid: that one row). Its key is a draft's (`<key>~only:<hash>`); stored, it becomes `v:<id>`. Throws when the
 * option is not one of the question's choices, or the question has none to narrow.
 */
export function narrowToOption(q, option) {
    const narrowable = q.type === 'select' || q.type === 'multi_select' || q.type === 'matrix';
    const canonical = narrowable ? matchChoice(questionChoices(q), option, q.option_aliases) : null;
    if (!canonical)
        throw new Error(`"${option}" is not one of the options of ${q.key}`);
    const wording = `${q.label}: ${canonical}`;
    return {
        key: `${q.key}~only:${fnv1a32hex(canonical).slice(0, 6)}`,
        question_key: q.key,
        wording: wording.slice(0, 300),
        agency_label: wording.slice(0, 200),
        input: q.type === 'matrix' ? 'default' : 'yes_no',
        narrow_to: [canonical],
        audience: 'any',
    };
}
/** The intake type a free-text way to ask uses: a long answer for lists, addresses and contacts. */
const freeTextType = (q) => q.type === 'long_text' || q.type === 'address' || q.type === 'contact' || q.type === 'multi_select' ? 'long_text' : 'short_text';
/** The longest intake field label (intakeFieldSchema). */
const LABEL_MAX = 200;
/**
 * A library (or agency) question asked one way, as the COMPLETE IntakeField every form runner, schema and input
 * renders (spec 3.20: a library form item is stored as exactly this). The type map is FIXED from Phase 1
 * (INTAKE_TYPE_OF_QUESTION: yes_no -> select ['Yes','No']; measurement and url -> short_text; address and contact ->
 * long_text; others keep their name); a free-text way uses a text field, a yes/no way a Yes/No select, and an
 * agency's own field keeps its own type and choices, so toIntakeField(q, agencyFieldVariant(q, f), f.id) renders
 * like f. Sensitive storage (secure, vault) is a sensitive field; an agency-owned question is read-only to the
 * talent. It names its question and way (`question_key`, `variant_key`) and carries the agency's `reason` when given.
 */
export function toIntakeField(q, v, fieldId, opts = {}) {
    const type = v.field_type ?? (v.input === 'text' ? freeTextType(q) : v.input === 'yes_no' ? 'select' : INTAKE_TYPE_OF_QUESTION[q.type]);
    const field = { id: fieldId, type, label: v.wording.slice(0, LABEL_MAX) };
    if (v.help)
        field.help = v.help;
    if (opts.required)
        field.required = true;
    if (type === 'select' || type === 'multi_select') {
        const options = wayChoices(q, v).map((c) => c.label);
        if (options.length)
            field.options = options;
    }
    if (type === 'matrix') {
        field.rows = wayChoices(q, v).map((c) => c.label);
        field.columns = [...(v.own_columns ?? q.columns ?? [])];
    }
    if (type === 'file')
        field.accept = q.storage === 'document' ? 'document' : 'image';
    if (q.storage === 'secure' || q.storage === 'vault')
        field.sensitive = true;
    if (q.owner === 'agency')
        field.access = 'client_read';
    field.question_key = q.key;
    field.variant_key = v.key;
    const reason = opts.reason?.trim().slice(0, MAX_ASK_REASON).trim();
    if (reason)
        field.reason = reason;
    return field;
}
//# sourceMappingURL=variants.js.map